import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  CheckboxButton,
  Title,
  Spinner,
  Datepicker,
  Dropdown,
  RadioButton,
} from '../../components/shared';
import rightIcon from '../../images/right-open-icon.png';
import downIcon from '../../images/down-close-icon.png';
import { downloadReport } from '../../apis';
import moment from 'moment';
import { showFeedback } from '../../store/ui/ui.actions';
import { Radio, Modal } from 'antd';
import { selectFarmsData } from '../../store/farms/farms.selector';
import { selectAccount } from '../../store/auth/auth.selector';
import { selectBoats } from '../../store/users/users.selector';
import { selectLang } from '../../store/ui/ui.selector';
import { translate } from '../../lib/lang.helper';
import { StageLabelSpan } from '../../components/CommonViews';
import {
  selectAssessmentFields,
  selectSeedingFields,
  selectHarvestFields,
} from '../../store/automation/automation.selector';
import ReportColumnSection, {
  ReportEntry,
  SEEDING_COLUMNS,
  HARVEST_COLUMNS,
  ASSESSMENT_COLUMNS,
  SPAT_STORAGE_COLUMNS,
  CUSTOM_REPORT_COLUMNS,
} from '../../components/todo/ReportColumnSection';

type LabelType = 'FARM' | 'LINE';

interface RowItemProps {
  isCollapsed: boolean | null;
  level: LabelType;
  label: string;
  isChecked: boolean;
  isFullChecked: boolean;
  toggleCollapse: (p: any) => void;
  toggleCheck: (p: any) => void;
  onClickLabel?: (p: any) => void;
}

const RowItem: React.FC<RowItemProps> = ({
  isCollapsed,
  level,
  label,
  isChecked,
  isFullChecked,
  toggleCollapse,
  toggleCheck,
  onClickLabel,
}) => {
  const exp_cal_class =
    isCollapsed === null
      ? 'empty'
      : isCollapsed === true
        ? 'collapsed'
        : 'expanded';

  return (
    <div className='row-item'>
      <div className={`level-${level.toLowerCase()}`}></div>
      <div className={`exp-col ${exp_cal_class}`} onClick={toggleCollapse}>
        {isCollapsed !== null &&
          (isCollapsed ? (
            <img src={downIcon} alt='right-icon' />
          ) : (
            <img src={rightIcon} alt='down-icon' />
          ))}
      </div>
      <div className='label'>
        <span onClick={onClickLabel}>{label}</span>
      </div>
      <div className='checker'>
        <CheckboxButton
          checked={isChecked}
          isNegative={!isFullChecked}
          label=''
          onChange={toggleCheck}
        />
      </div>
    </div>
  );
};

type TReport =
  | 'FARM'
  | 'TASK'
  | 'SEEDING'
  | 'HARVEST'
  | 'ASSESSMENT'
  | 'BUDGET'
  | 'FLOAT'
  | 'INSURANCE'
  | 'TIME_SHEET'
  | 'PRODUCTIVITY'
  | 'CROPS'
  | 'BUDGET_DETAIL'
  | 'SPAT_STORAGE'
  | 'SPAT_NURSERY'
  | 'FARM_GROWING'
  | 'SUMMARY'
  | 'CUSTOM';
type TPeriod = 'TODAY' | 'LAST_7' | 'LAST_30' | 'LAST_MONTH' | 'YEAR';

const rptOptions = [
  {
    id: 'farm',
    value: 'FARM',
    label: 'Farm Overview',
  },
  {
    id: 'task',
    value: 'TASK',
    label: 'Task Report',
  },
  {
    id: 'seeding',
    value: 'SEEDING',
    label: 'Seeding Report',
  },
  {
    id: 'harvest',
    value: 'HARVEST',
    label: 'Harvest Report',
  },
  {
    id: 'assessment',
    value: 'ASSESSMENT',
    label: 'Assessment Report',
  },
  {
    id: 'float',
    value: 'FLOAT',
    label: 'Float Report',
  },
  {
    id: 'insurance',
    value: 'INSURANCE',
    label: 'Insurance Report',
  },
  {
    id: 'time_sheet',
    value: 'TIME_SHEET',
    label: 'Boat Timesheet',
  },
  {
    id: 'productivity',
    value: 'PRODUCTIVITY',
    label: 'Productivity Report',
  },
  {
    id: 'spat_storage',
    value: 'SPAT_STORAGE',
    label: 'Spats Report',
  },
  {
    id: 'spat_nursery',
    value: 'SPAT_NURSERY',
    label: 'Spat Nursery Report',
  },
  { id: 'crops', value: 'CROPS', label: 'Crops Report' },
  { id: 'farm-growing', value: 'FARM_GROWING', label: 'Growing Overall' },
  { id: 'summary', value: 'SUMMARY', label: 'Summary Report' },
];

const budgetOptions = [
  {
    id: 'budget',
    value: 'BUDGET',
    label: 'Budget Report',
  },
  {
    id: 'budget_detail',
    value: 'BUDGET_DETAIL',
    label: 'Budget Details',
  },
];

const periodTypeOptions = [
  {
    id: 'today',
    value: 'TODAY',
    label: 'Today',
  },
  {
    id: 'last_7',
    value: 'LAST_7',
    label: 'Last 7 days',
  },
  {
    id: 'last_30',
    value: 'LAST_30',
    label: 'Last 30 days',
  },
  {
    id: 'last_month',
    value: 'LAST_MONTH',
    label: 'Last month',
  },
  {
    id: 'year',
    value: 'YEAR',
    label: 'Year to date',
  },
];

const harvestFinalOptions = [
  { id: 'final', value: 'final', label: 'Final harvest' },
  { id: 'non_final', value: 'non_final', label: 'Reseeding' },
  { id: 'all', value: 'all', label: 'All' },
];

const nonPeriodReports = [
  'FLOAT',
  'INSURANCE',
  'FARM',
  'SPAT_STORAGE',
  'SPAT_NURSERY',
  'CROPS',
  'BUDGET_DETAIL',
  'FARM_GROWING',
  'CUSTOM',
];

const ReportPage = () => {
  const dispatch = useDispatch<any>();
  const lang = useSelector(selectLang);
  const farms = useSelector(selectFarmsData);
  const boatsData = useSelector(selectBoats);
  const accSetting = useSelector(selectAccount);
  const showBudget = accSetting?.show_budget_menu;
  const detailedBudgetEnabled = accSetting?.detailed_budget_enabled;
  const reportTypeOptions = [
    ...rptOptions,
    {
      id: 'custom',
      value: 'CUSTOM',
      label: accSetting?.custom_report_name ?? 'Custom Report',
    },
  ];
  const reportTypes = showBudget
    ? detailedBudgetEnabled
      ? [...reportTypeOptions, ...budgetOptions]
      : [...reportTypeOptions, budgetOptions[0]]
    : reportTypeOptions;

  const [checkedFarmIds, setCheckedFarmIds] = useState<number[]>(
    farms.map(x => Number(x.id)),
  );
  const [checkedLineIds, setCheckedLineIds] = useState<number[]>(
    farms.reduce(
      (p, c) => [...p, ...c.lines.map(l => Number(l.id))],
      [] as number[],
    ),
  );
  const [boatIDs, setBoatIDs] = useState<number[]>(
    boatsData.map(x => x.id) || [],
  );
  const [isFarmCollapsed, setIsFarmCollapsed] = useState<any>({});
  const [previewLink, setPreviewLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    rangeFrom: new Date(new Date().getFullYear(), 0, 1).getTime(),
    rangeTo: Date.now(),
  });
  const [selectedOwner, setSelectedOwner] = useState<string>();

  const updateDateRange = (key: string, val: number) => {
    setDateRange({ ...dateRange, [key]: val });
  };
  const assessmentFields = useSelector(selectAssessmentFields);
  const seedingFields = useSelector(selectSeedingFields);
  const harvestFields = useSelector(selectHarvestFields);
  const [farmVisual, setFarmVisual] = useState(false);
  const [reportType, setReportType] = useState<TReport>('FARM');
  const [period, setPeriod] = useState<TPeriod>('YEAR');
  const [fileType, setFileType] = useState<'pdf' | 'xlsx'>('pdf');
  const [hvFinal, setHvFinal] = useState('all');
  const [stages, setStages] = useState<number[]>([]);
  const [templateModal, setTemplateModal] = useState(false);

  const templateEntry = ((): ReportEntry | null => {
    switch (reportType) {
      case 'SEEDING':
        return {
          title: '_seeding_report_columns',
          columnKey: 'seeding_report_columns',
          allColumns: [...SEEDING_COLUMNS, ...seedingFields.map(f => f.name)],
          dynamicColumns: seedingFields.map(f => f.name),
        };
      case 'HARVEST':
        return {
          title: '_harvest_report_columns',
          columnKey: 'harvest_report_columns',
          allColumns: [...HARVEST_COLUMNS, ...harvestFields.map(f => f.name)],
          dynamicColumns: harvestFields.map(f => f.name),
        };
      case 'ASSESSMENT':
        return {
          title: '_assess_report_columns',
          columnKey: 'assessment_report_columns',
          allColumns: [
            ...ASSESSMENT_COLUMNS,
            ...assessmentFields.map(f => f.name),
          ],
          dynamicColumns: assessmentFields.map(f => f.name),
        };
      case 'SPAT_STORAGE':
        return {
          title: '_spat_storage_report_columns',
          columnKey: 'spat_storage_report_columns',
          allColumns: SPAT_STORAGE_COLUMNS,
        };
      case 'CUSTOM':
        return {
          title: '_custom_report_columns',
          columnKey: 'custom_report_columns',
          allColumns: [
            ...CUSTOM_REPORT_COLUMNS,
            ...seedingFields.map(f => f.name),
            ...assessmentFields.map(f => f.name),
            ...harvestFields.map(f => f.name),
          ],
          dynamicGroups: {
            seeding: seedingFields.map(f => f.name),
            assessment: assessmentFields.map(f => f.name),
            harvest: harvestFields.map(f => f.name),
          },
          noTabs: true,
        };
      default:
        return null;
    }
  })();

  const updatePeriod = (p: TPeriod) => {
    setPeriod(p);
    if (p === 'TODAY') {
      setDateRange({
        rangeFrom: moment().toDate().getTime(),
        rangeTo: moment().toDate().getTime(),
      });
    } else if (p === 'LAST_7') {
      setDateRange({
        rangeFrom: moment().subtract(7, 'd').toDate().getTime(),
        rangeTo: moment().toDate().getTime(),
      });
    } else if (p === 'LAST_30') {
      setDateRange({
        rangeFrom: moment().subtract(30, 'd').toDate().getTime(),
        rangeTo: moment().toDate().getTime(),
      });
    } else if (p === 'LAST_MONTH') {
      setDateRange({
        rangeFrom: moment()
          .subtract(1, 'month')
          .startOf('month')
          .toDate()
          .getTime(),
        rangeTo: moment()
          .subtract(1, 'month')
          .endOf('month')
          .toDate()
          .getTime(),
      });
    } else if (p === 'YEAR') {
      setDateRange({
        rangeFrom: moment().startOf('year').toDate().getTime(),
        rangeTo: moment().toDate().getTime(),
      });
    }
  };
  const isFarmChecked = (k: number) => {
    return checkedFarmIds.includes(k);
  };
  const isLineChecked = (k: number) => {
    return checkedLineIds.includes(k);
  };
  const isFarmFullChecked = (i: number) => {
    const lineIds = farms.find(t => t.id === i)?.lines.map(x => x.id);
    return (lineIds ?? []).every(k => isLineChecked(parseInt(k.toString())));
  };
  const toggleLineCheck = (f: number, k: number) => {
    const tmp = [...checkedLineIds];
    const i = tmp.findIndex(x => x === k);
    if (i < 0) {
      tmp.push(k);
      if (!checkedFarmIds.some(x => x === f)) {
        setCheckedFarmIds([...checkedFarmIds, f]);
      }
    } else {
      tmp.splice(i, 1);
      const farm = farms.find(x => x.id === f);
      if (!farm?.lines.some(x => tmp.some(y => y === x.id))) {
        setCheckedFarmIds(checkedFarmIds.filter(x => x !== f));
      }
    }
    setCheckedLineIds(tmp);
  };
  const toggleFarmCheck = (k: number) => {
    const tmp = [...checkedFarmIds];
    const i = tmp.findIndex(x => x === k);
    const lineIds =
      farms.find(x => x.id === k)?.lines.map(l => parseInt(l.id.toString())) ??
      [];
    if (i < 0) {
      tmp.push(k);
      setCheckedFarmIds(tmp);
      setCheckedLineIds([...checkedLineIds, ...lineIds]);
    } else {
      const lTmp = [...checkedLineIds];
      if (lineIds.every(l => checkedLineIds.findIndex(x => x === l) >= 0)) {
        tmp.splice(i, 1);
        setCheckedFarmIds(tmp);
        for (let x of lineIds)
          lTmp.splice(
            lTmp.findIndex(l => l === x),
            1,
          );
      } else {
        for (let x of lineIds.filter(l => lTmp.findIndex(x => x === l) < 0)) {
          lTmp.push(x);
        }
      }
      setCheckedLineIds(lTmp);
    }
  };
  const isCheckedFarmAll = () => {
    return farms.every(f => isFarmFullChecked(parseInt(f.id.toString())));
  };
  const toggleFarmCheckAll = () => {
    if (farms.every(f => isFarmFullChecked(parseInt(f.id.toString())))) {
      setCheckedFarmIds([]);
      setCheckedLineIds([]);
    } else {
      setCheckedFarmIds(farms.map(f => parseInt(f.id.toString())));
      const tmp = [];
      for (let f of farms) {
        const ls = f.lines.map(l => parseInt(l.id.toString()));
        tmp.push(...ls);
      }
      setCheckedLineIds(tmp);
    }
  };
  const toggleFarmCollapse = (farmId: number) => {
    let cf: any = { ...isFarmCollapsed };
    if (cf[farmId]) cf[farmId] = false;
    else cf[farmId] = true;
    setIsFarmCollapsed(cf);
  };
  const requestReport = async (type: 'download' | 'preview') => {
    if (type === 'preview' && /iPad|iPhone/.test(navigator.platform)) {
      alert('Sorry, iPhone or iPad does not support preview feature well');
    }
    setLoading(true);
    setPreviewLink(null);
    const mode =
      reportType === 'FARM'
        ? `farm-overview?type=${farmVisual ? 'visual' : 'normal'}`
        : reportType.toLowerCase();
    let data: any = {
      farms: checkedFarmIds,
      lines: checkedLineIds,
      boats: boatIDs,
      range_from:
        reportType === 'TASK'
          ? moment(dateRange.rangeFrom).format('yyyy-MM-DD')
          : Math.floor(dateRange.rangeFrom / 1000),
      range_to:
        reportType === 'TASK'
          ? moment(dateRange.rangeTo).add(1, 'day').format('yyyy-MM-DD')
          : Math.floor(dateRange.rangeTo / 1000),
      file_type: fileType,
      stages,
    };
    if (reportType === 'HARVEST' && hvFinal !== 'all') {
      data.is_final = hvFinal === 'final';
    }
    try {
      const res = await downloadReport(data, mode);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      let url = window.URL.createObjectURL(
        fileType === 'pdf' ? blob : res.data,
      );
      if (type === 'download') {
        let link = document.createElement('a');
        link.href = url;
        link.download = `report.${fileType}`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } else {
        setPreviewLink(url);
      }
    } catch (err: any) {
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'error',
          message: translate(lang, err?.message ?? 'Something went wrong.'),
        }),
      );
    }
    setLoading(false);
  };
  const selectOwner = (v: string | null) => {
    if (!v) {
      setCheckedFarmIds(farms.map(x => Number(x.id)));
      setSelectedOwner(undefined);
    } else {
      const selectedFarms = farms.filter(f =>
        f.owners.some(o => o.title === v),
      );
      setCheckedFarmIds(selectedFarms.map(x => Number(x.id)));
      setSelectedOwner(v);
    }
  };

  const owners = farms
    .reduce(
      (prv, cur) => [...prv, cur.owners.map(o => o.title)].flat(),
      [] as string[],
    )
    .filter((v, i, a) => v && a.indexOf(v) === i);

  return (
    <div className='h-calc-80 bg-secondary'>
      <div className='container pos-relative'>
        <div className='report-page'>
          <div className='report--header d-flex justify-between align-center'>
            <Title size={5} color='black-3' align='default' fontWeight={700}>
              {translate(lang, 'Report')}
            </Title>
          </div>
          <div className='report--body'>
            <div className='farm-line'>
              <div className='report-options'>
                <div className='flex-1 mr-7'>
                  <Dropdown
                    label='Report type'
                    defaultValue={reportType}
                    options={reportTypes.map(x => ({
                      ...x,
                      label: translate(lang, x.label),
                    }))}
                    onChange={v => setReportType(v as any)}
                  />
                </div>
                {templateEntry && (
                  <div
                    className='ml-7'
                    style={{ display: 'flex', alignItems: 'flex-end' }}
                  >
                    <Button
                      color='green'
                      size={2}
                      width='wide'
                      type='bordered'
                      onClick={() => setTemplateModal(true)}
                    >
                      {translate(lang, 'Template')}
                    </Button>
                  </div>
                )}
              </div>
              <div className='mb-17 ml-6'>
                <Dropdown
                  label={translate(lang, 'Filter by owner')}
                  options={owners.map(o => ({ id: o, value: o, label: o }))}
                  value={selectedOwner}
                  onChange={v => selectOwner(v)}
                  showSearch={true}
                  allowClear={true}
                />
              </div>
              {!nonPeriodReports.includes(reportType) && (
                <div className='mb-17 ml-6'>
                  <Dropdown
                    label={translate(lang, 'Period')}
                    defaultValue={period}
                    options={periodTypeOptions.map(x => ({
                      ...x,
                      label: translate(lang, x.label),
                    }))}
                    onChange={v => updatePeriod(v as any)}
                  />
                </div>
              )}
              {!nonPeriodReports.includes(reportType) && (
                <div className='date-range'>
                  <Datepicker
                    label={translate(
                      lang,
                      ['TIME_SHEET', 'ASSESSMENT', 'CUSTOM'].includes(
                        reportType,
                      )
                        ? 'Date from'
                        : 'Seeded from',
                    )}
                    defaultValue={dateRange.rangeFrom}
                    onChange={e =>
                      updateDateRange(
                        'rangeFrom',
                        e?.toDate().getTime() ?? Date.now(),
                      )
                    }
                    className='mr-7'
                  />
                  <Datepicker
                    label={translate(
                      lang,
                      ['TIME_SHEET', 'ASSESSMENT', 'CUSTOM'].includes(
                        reportType,
                      )
                        ? 'Date to'
                        : 'Seeded to',
                    )}
                    defaultValue={dateRange.rangeTo}
                    onChange={e =>
                      updateDateRange(
                        'rangeTo',
                        e?.toDate().getTime() ?? Date.now(),
                      )
                    }
                    className='ml-7'
                  />
                </div>
              )}
              {reportType === 'HARVEST' && (
                <div className='mb-17 ml-7 mr-7'>
                  <Dropdown
                    label={translate(lang, 'Harvest Type')}
                    options={harvestFinalOptions}
                    value={hvFinal}
                    onChange={v => setHvFinal(v)}
                  />
                </div>
              )}
              {(reportType === 'CROPS' || reportType === 'SPAT_STORAGE') && (
                <div className='mb-17 ml-7 mr-7'>
                  <Dropdown
                    mode='multiple'
                    label={translate(lang, 'Stages')}
                    options={[1, 2, 3, 4].map(x => ({
                      id: x.toString(),
                      value: x.toString(),
                      label: <StageLabelSpan stage={x} />,
                    }))}
                    value={stages.map(String) as any}
                    onChange={(v: any) => v && setStages(v.map(Number))}
                  />
                </div>
              )}
              <div className='row-item' style={{ marginBottom: '17px' }}>
                <Radio.Group
                  className='d-flex justify-between w-100'
                  value={fileType}
                  onChange={e => setFileType(e.target.value)}
                >
                  <RadioButton value={'pdf'} label='PDF' />
                  <RadioButton value={'xlsx'} label='Excel' />
                </Radio.Group>
              </div>
              <div
                style={{ maxHeight: '90vh', overflow: 'auto' }}
                role='region'
                aria-label='Report selection options'
              >
                {['TIME_SHEET', 'PRODUCTIVITY'].includes(reportType) ? (
                  boatsData.map(boat => (
                    <div key={boat.id}>
                      <RowItem
                        isCollapsed={null}
                        level='FARM'
                        label={`${boat.name} ${boat.reg_number}`}
                        isChecked={boatIDs.includes(boat.id)}
                        isFullChecked={boatIDs.includes(boat.id)}
                        toggleCollapse={() => null}
                        toggleCheck={() =>
                          setBoatIDs(
                            boatIDs.includes(boat.id)
                              ? boatIDs.filter(x => x !== boat.id)
                              : [...boatIDs, boat.id],
                          )
                        }
                        onClickLabel={() =>
                          setBoatIDs(
                            boatIDs.includes(boat.id)
                              ? boatIDs.filter(x => x !== boat.id)
                              : [...boatIDs, boat.id],
                          )
                        }
                      />
                    </div>
                  ))
                ) : (
                  <>
                    {reportType === 'FARM' && (
                      <RowItem
                        isCollapsed={null}
                        level='FARM'
                        label={translate(lang, 'Include farm visuals')}
                        isChecked={farmVisual}
                        isFullChecked={farmVisual}
                        toggleCollapse={() => null}
                        toggleCheck={() => setFarmVisual(!farmVisual)}
                        onClickLabel={() => setFarmVisual(!farmVisual)}
                      />
                    )}
                    <RowItem
                      isCollapsed={null}
                      level='FARM'
                      label={translate(lang, 'Select all farms')}
                      isChecked={checkedFarmIds.length > 0}
                      isFullChecked={isCheckedFarmAll()}
                      toggleCollapse={() => null}
                      toggleCheck={() => toggleFarmCheckAll()}
                      onClickLabel={() => toggleFarmCheckAll()}
                    />
                    {farms.map(farm => (
                      <div key={farm.id}>
                        <RowItem
                          isCollapsed={
                            !!isFarmCollapsed[parseInt(farm.id.toString())]
                          }
                          level='FARM'
                          label={`${farm.name} - ${farm.farm_number}`}
                          isChecked={isFarmChecked(
                            parseInt(farm.id.toString()),
                          )}
                          isFullChecked={isFarmFullChecked(
                            parseInt(farm.id.toString()),
                          )}
                          toggleCollapse={() =>
                            toggleFarmCollapse(parseInt(farm.id.toString()))
                          }
                          toggleCheck={() =>
                            toggleFarmCheck(parseInt(farm.id.toString()))
                          }
                          onClickLabel={() => {
                            toggleFarmCheck(parseInt(farm.id.toString()));
                          }}
                        />
                        {!!isFarmCollapsed[parseInt(farm.id.toString())] &&
                          farm.lines.length > 0 &&
                          farm.lines.map(line => (
                            <div key={line.id}>
                              <RowItem
                                label={translate(
                                  lang,
                                  'Line - %s',
                                  line.line_name,
                                )}
                                isCollapsed={null}
                                level='LINE'
                                isChecked={isLineChecked(
                                  parseInt(line.id.toString()),
                                )}
                                isFullChecked={true}
                                toggleCollapse={() => null}
                                toggleCheck={() =>
                                  toggleLineCheck(
                                    parseInt(farm.id.toString()),
                                    parseInt(line.id.toString()),
                                  )
                                }
                                onClickLabel={() =>
                                  toggleLineCheck(
                                    parseInt(farm.id.toString()),
                                    parseInt(line.id.toString()),
                                  )
                                }
                              />
                            </div>
                          ))}
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
            <div className='render-content'>
              <div className='render-header'>
                <Button
                  className='mr-17'
                  color='blue'
                  size={2}
                  width='wide'
                  type='fill'
                  disabled={fileType === 'xlsx'}
                  onClick={e => requestReport('preview')}
                >
                  {translate(lang, 'Preview')}
                </Button>
                <Button
                  className='ml-17 mr-17'
                  color='green'
                  size={2}
                  width='wide'
                  type='fill'
                  onClick={e => requestReport('download')}
                >
                  {translate(lang, 'Download')}
                </Button>
              </div>
              <div className='render-body'>
                {loading ? (
                  <div className='mt-64'>
                    <Spinner />
                  </div>
                ) : (
                  previewLink && (
                    <iframe
                      src={previewLink}
                      frameBorder={0}
                      className='preview-pdf'
                    ></iframe>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      {templateEntry && (
        <Modal
          visible={templateModal}
          onCancel={() => setTemplateModal(false)}
          footer={null}
          width={600}
        >
          <ReportColumnSection
            key={templateEntry.columnKey}
            title={templateEntry.title}
            columnKey={templateEntry.columnKey}
            allColumns={templateEntry.allColumns}
            dynamicColumns={templateEntry.dynamicColumns}
            dynamicGroups={templateEntry.dynamicGroups}
            noTabs={templateEntry.noTabs}
            onBack={() => setTemplateModal(false)}
            onSaveSuccess={() => setTemplateModal(false)}
            inModal
          />
        </Modal>
      )}
    </div>
  );
};

export default ReportPage;
